<template>
  <div class="space-y-6">
    <Card>
      <h3 class="text-lg font-medium text-text mb-4">General Settings</h3>

      <OrganizationForm
        :organization="organization"
        :is-loading="isLoading"
        :disabled-jit="!canVerifyDomain"
        @submit="handleUpdate"
      />
    </Card>

    <Card v-if="canDelete" class="border-t-4 border-t-danger-500">
      <h3 class="text-lg font-medium text-danger-600 mb-4">Danger Zone</h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Permanently delete this organization and all its data. This action cannot be undone.
      </p>
      <Button variant="danger" @click="showDeleteDialog = true">
        Delete Organization
      </Button>
    </Card>

    <!-- Modals -->
    <OrganizationDeleteDialog
      v-model:open="showDeleteDialog"
      :organization="organization"
      :is-loading="isLoading"
      @confirm="handleDelete"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Card, Button } from '@asabdev/asab-ui';
  import {
    useOrganization,
    useOrganizationPermissions,
    useOrganizationContext,
  } from '../composables';
  import type { CreateOrganizationRequest, UpdateOrganizationRequest } from '../types';
  import OrganizationForm from './OrganizationForm.vue';
  import OrganizationDeleteDialog from './OrganizationDeleteDialog.vue';
  import { useRouter } from 'vue-router';

  const router = useRouter();
  const { organization, updateOrganization, deleteOrganization, isLoading } = useOrganization();
  const { isOwner } = useOrganizationPermissions();
  const { refreshOrganizations } = useOrganizationContext();

  const showDeleteDialog = ref(false);
  const canVerifyDomain = computed(() => isOwner.value);
  const canDelete = computed(() => isOwner.value);

  const handleUpdate = async (data: CreateOrganizationRequest | UpdateOrganizationRequest) => {
    try {
      await updateOrganization(data as UpdateOrganizationRequest);
    } catch (error) {
      console.error('Failed to update organization:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOrganization();
      showDeleteDialog.value = false;
      await refreshOrganizations();
      router.push('/app/organizations');
    } catch (error) {
      console.error('Failed to delete organization:', error);
    }
  };
</script>
